import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Input, Button } from 'antd';
import styled from 'styled-components';
import { Box, Text } from 'atoms';

const { TextArea } = Input;

const ModalContent = styled(Box)`
	text-align: center;
`;

const ModalTitle = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #000;
	display: block;
	margin-bottom: 16px;
`;

const ModalMessage = styled(Text)`
	font-size: 14px;
	color: #666;
	display: block;
	margin-bottom: 24px;
	line-height: 1.5;
`;

const ButtonGroup = styled(Box)`
	display: flex;
	justify-content: center;
	gap: 12px;
	margin-top: 24px;
`;

const CancelButton = styled(Button)`
	border-color: #1890ff;
	color: #1890ff;
	background: #fff;
	min-width: 100px;
`;

const ConfirmButton = styled(Button)`
	min-width: 100px;
`;

/**
 * Approve / Reject dialogs for a Driver or Vehicle review.
 * Approve: comments optional. Reject: a reason is required (the vendor sees why).
 * `mode` is 'approve', 'reject' or null (closed).
 */
export default function VerificationDecisionModals({ mode, noun, loading, onConfirm, onClose }) {
	const [text, setText] = useState('');

	useEffect(() => {
		if (mode) setText('');
	}, [mode]);

	const isReject = mode === 'reject';
	const canConfirm = !isReject || text.trim().length > 0;

	return (
		<Modal
			open={Boolean(mode)}
			onCancel={onClose}
			footer={null}
			width={500}
			centered
			destroyOnClose
		>
			<ModalContent>
				<ModalTitle>
					{isReject ? `Reject ${noun} Documents` : `Approve ${noun} Documents`}
				</ModalTitle>
				<ModalMessage>
					{isReject
						? `The ${noun.toLowerCase()} will be marked rejected and its trips cannot be closed until the documents are corrected and approved.`
						: `Confirm that all ${noun.toLowerCase()} documents are valid. Trips on this ${noun.toLowerCase()} can then be closed.`}
				</ModalMessage>
				<Box textAlign="left">
					<Text fontSize="14px" color="#333" mb={1} display="block">
						{isReject ? 'Rejection Reason' : 'Comments (optional)'}
						{isReject && <span style={{ color: '#ff4d4f' }}> *</span>}
					</Text>
					<TextArea
						rows={4}
						value={text}
						onChange={(e) => setText(e.target.value)}
						placeholder={isReject ? 'Enter rejection reason' : 'Enter comments'}
						maxLength={500}
						showCount
					/>
				</Box>
				<ButtonGroup>
					<CancelButton onClick={onClose}>Cancel</CancelButton>
					<ConfirmButton
						type="primary"
						danger={isReject}
						loading={loading}
						disabled={!canConfirm}
						onClick={() => onConfirm(text.trim())}
					>
						{isReject ? 'Reject' : 'Approve'}
					</ConfirmButton>
				</ButtonGroup>
			</ModalContent>
		</Modal>
	);
}

VerificationDecisionModals.propTypes = {
	mode: PropTypes.oneOf(['approve', 'reject', null]),
	noun: PropTypes.string.isRequired,
	loading: PropTypes.bool,
	onConfirm: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

VerificationDecisionModals.defaultProps = {
	mode: null,
	loading: false,
};
